import StudyList, { LessonItem, UnitItem } from "@/components/course/StudyList";
import {
  findLessonInCourse,
  getFirstLessonUnit,
  toStudyListTerms,
} from "@/service/course.mappers";
import type { CourseLesson, CourseUnit } from "@/types/course.model";
import Breadcrumbs from "@/components/design-system/breadcrumbs";
import FilterWithRadio from "@/components/design-system/FilterWithRadio";
import { getStudentsDropdownService } from "@/service/base.service";
import { AppDispatch } from "@/store";
import { selectUser, selectUserRole } from "@/store/slices/authSlice";
import {
  fetchCourseById,
  fetchUnitScormFile,
  refetchLessonUnitCount,
  resetCourseDetail,
  resetCurrentCourse,
  selectCourseLoading,
  selectCourseTabCount,
  selectCurrentCourse,
  selectNavigationSource,
  selectNoCourseData,
  updateScormStatusAction,
} from "@/store/slices/courseSlice";
import { ROLES } from "@/utils/constants";
import {
  buildScormUpdateStatusPayload,
  resolveLinkedCourseGradeId,
} from "@/utils/scormWebhookPayload";
import { useEffect, useMemo, useRef, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import Assignments from "../components/Assignments";
import Queries from "../components/Queries";
import ScromFile from "../components/scromFile";
import { Button } from "@/components/design-system/button";
import Evaluation from "../components/Evaluation";
import {
  resolveCsViewType,
} from "../utils/csViewContext";

type CourseDetailLocationState = {
  selectedTab?: string;
  selectedStudentId?: string;
  assignmentType?: string;
  lessonId?: string | null;
  unitId?: string | null;
  viewType?: "course" | "class";
  /** Class view: linked LMS course uuid when not on CS view payload */
  courseGradeId?: string;
};

// Define a function to get header menus based on source
const getHeaderMenus = (navigationSource: string, showAssignment: boolean) => {
  // Base menu that's always shown
  const baseMenus = [
    {
      title: "Unit",
      key: "unit",
    },
  ];

  if (showAssignment) {
    baseMenus.push(
      {
        title: "Assignment",
        key: "assignment",
      },
      {
        title: "Queries",
        key: "feedback",
      },
      {
        title: "Evaluation",
        key: "evaluation",
      }
    );
  }

  // Only add assignment and feedback if not coming from course list
  // if (isStudent || navigationSource !== "course-list" && navigationSource !== "student-list") {
  //   baseMenus.push(
  //     {
  //       title: "Assignment",
  //       key: "assignment",
  //     },
  //     {
  //       title: "Queries",
  //       key: "feedback",
  //     }
  //   );
  // }

  return baseMenus;
};

function CourseDetail() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const courseData = useSelector(selectCurrentCourse, shallowEqual);
  const loading = useSelector(selectCourseLoading, shallowEqual);
  const navigationSource = useSelector(selectNavigationSource, shallowEqual);
  const noCourseData = useSelector(selectNoCourseData, shallowEqual);

  const locationState = (location.state as CourseDetailLocationState | null) ?? {};
  const [searchParams] = useSearchParams();
  const viewType = resolveCsViewType(searchParams, locationState.viewType);
  const lastScormWebhookKey = useRef<string | null>(null);

  const [selectedLesson, setSelectedLesson] = useState<LessonItem | null>(null);
  const [selectedUnit, setSelectedUnit] = useState<UnitItem | null>(null);

  const [selectedTab, setSelectedTab] = useState<string>(
    locationState.selectedTab ?? "unit"
  );

  const [selectedAssignmentType, setSelectedAssignmentType] = useState<string>(
    locationState.assignmentType || "general"
  );
  const [students, setStudents] = useState<
    { id: string; user_id: string; name: string }[]
  >([]);
  const [selectedStudentId, setSelectedStudentId] = useState<string>("");
  const user = useSelector(selectUser, shallowEqual);
  const userRole = useSelector(selectUserRole, shallowEqual);

  const courseTabCount = useSelector(selectCourseTabCount, shallowEqual);

  useEffect(() => {
    if (viewType !== "class" || !id) return;
    if (searchParams.get("viewType") === "class") return;

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("viewType", "class");
    navigate(
      { pathname: location.pathname, search: `?${nextParams.toString()}` },
      { replace: true, state: location.state }
    );
  }, [id, viewType, searchParams, navigate, location.pathname, location.state]);

  useEffect(() => {
    lastScormWebhookKey.current = null;
  }, [id, viewType]);

  // Get the appropriate header menus based on navigation source
  const headerMenus = getHeaderMenus(
    navigationSource,
    userRole?.code === ROLES.STUDENT ||
    userRole?.code === ROLES.TEACHER ||
    userRole?.code === ROLES.SUPER_ADMIN
  );

  const transformedTerms = courseData ? toStudyListTerms(courseData) : [];
  const transformedLessons = transformedTerms[0]?.lessons ?? [];

  const toUnitItem = (unit: CourseUnit): UnitItem => ({
    id: unit.id,
    name: unit.name,
    status: unit.status ?? undefined,
    is_active: unit.is_active,
    notify: unit.notify ?? { assignment: 0, query: 0 },
    is_completed: unit.is_completed,
    duration: unit.duration,
  });

  const toLessonItem = (lesson: CourseLesson): LessonItem => ({
    id: lesson.id,
    name: lesson.name,
    units: lesson.units.map((unit) => toUnitItem(unit)),
    isExpanded: true,
  });

  const fetchStudents = async () => {
    try {
      const {
        data: { data },
      } = await getStudentsDropdownService({
        gradeId: id as string,
        // schoolId: user?.school_id,
        teacherId: user?.teacher_id,
      });

      setStudents(data);

      if (
        locationState.selectedStudentId
      ) {
        setSelectedStudentId(locationState.selectedStudentId);
      } else if (data.length > 0) {
        setSelectedStudentId(data[0].user_id);
      }
    } catch (error) {
      console.error("Error fetching students:", error);
    }
  };

  // Use a ref to track if we've already fetched the course data
  const hasFetchedCourse = useRef(false);

  // Effect to fetch course details when component mounts or id changes
  useEffect(() => {
    if (id && !hasFetchedCourse.current) {
      // Set the ref to true to prevent multiple fetches
      hasFetchedCourse.current = true;

      // First fetch students if teacher role
      if (userRole?.code === ROLES.TEACHER) {
        fetchStudents().then(() => {
          // After students are fetched and selectedStudentId is set, fetch course details
          dispatch(
            fetchCourseById({
              gradeId: id,
              studentId:
                selectedStudentId || locationState.selectedStudentId || null,
              viewType,
            })
          ).catch((error) => {
            console.error("Error fetching course details:", error);
          });
        });
      } else {
        // For non-teacher roles, fetch course directly
        dispatch(
          fetchCourseById({
            gradeId: id,
            studentId: locationState.selectedStudentId || null,
            viewType,
          })
        ).catch((error) => {
          console.error("Error fetching course details:", error);
        });
      }

      if (userRole?.code === ROLES.SUPER_ADMIN) {
        setSelectedStudentId(locationState.selectedStudentId ?? "");
      }
    }

    // Cleanup function to reset current course when component unmounts
    return () => {
      dispatch(resetCurrentCourse());
      dispatch(resetCourseDetail());
      // Reset the ref when component unmounts
      hasFetchedCourse.current = false;
    };
  }, [id, userRole, locationState.selectedStudentId, viewType, dispatch]);

  // Effect to set initial selected lesson and unit when course data loads
  useEffect(() => {
    if (!courseData) return;
    const first = getFirstLessonUnit(courseData);
    if (!first) return;

    const { lesson, unit } = first;
    setSelectedLesson(toLessonItem(lesson));
    setSelectedUnit(toUnitItem(unit));
  }, [courseData]);

  useEffect(() => {
    if (location.state?.lessonId && location.state?.unitId) {
      const lesson = courseData
        ? findLessonInCourse(courseData, location.state.lessonId)
        : undefined;
      if (lesson) {
        setSelectedLesson(toLessonItem(lesson));
        const unit = lesson.units.find(
          (unit) => unit.id === location.state.unitId
        );
        if (unit) {
          setSelectedUnit(toUnitItem(unit));
        }
      }
    }
  }, [location.state?.lessonId, location.state?.unitId, courseData]);

  const linkedCourseGradeId = useMemo(
    () =>
      resolveLinkedCourseGradeId(
        courseData,
        viewType,
        locationState.courseGradeId
      ),
    [courseData, viewType, locationState.courseGradeId]
  );

  // Load SCORM iframe URL whenever the selected unit changes (all roles).
  useEffect(() => {
    if (!selectedUnit?.id) return;
    dispatch(fetchUnitScormFile(selectedUnit.id));
  }, [selectedUnit?.id, dispatch]);

  // Student-only progress webhook when lesson, unit, and (for class) view data are ready.
  useEffect(() => {
    if (!selectedUnit?.id || !selectedLesson?.id) return;
    if (user?.role?.code !== ROLES.STUDENT) return;
    if (!id || !user.student_user_id) return;
    if (viewType === "class" && !courseData?.id) return;

    const webhookKey = `${viewType}:${selectedUnit.id}:${selectedLesson.id}`;
    if (lastScormWebhookKey.current === webhookKey) return;

    const payload = buildScormUpdateStatusPayload({
      viewType,
      resourceId: id,
      linkedCourseId: linkedCourseGradeId,
      lessonId: selectedLesson.id,
      unitId: selectedUnit.id,
      userId: user.student_user_id,
      status: "COMPLETED",
    });

    if (!payload) return;

    lastScormWebhookKey.current = webhookKey;
    dispatch(updateScormStatusAction(payload));
  }, [
    courseData?.id,
    dispatch,
    id,
    linkedCourseGradeId,
    selectedLesson?.id,
    selectedUnit?.id,
    user?.role?.code,
    user?.student_user_id,
    viewType,
  ]);

  const onUnitSelect = (lesson: LessonItem, unit: UnitItem) => {
    setSelectedLesson(lesson);
    setSelectedUnit(unit);
  };

  const getCount = (key: string) => {
    switch (key) {
      case "assignment":
        return (
          parseInt(
            (courseTabCount?.assignment?.general_assignment as string) || "0"
          ) +
          parseInt(
            (courseTabCount?.assignment?.unit_assignment as string) || "0"
          )
        );
      case "feedback":
        return courseTabCount?.query || 0;
      default:
        return null;
    }
  };

  if (noCourseData || loading) {
    return (
      <div className="h-full flex flex-col gap-6">
        <div className="h-full m-4">
          <div className="bg-white rounded-[12px] px-4 sm:px-6 md:px-[32px] w-full overflow-hidden">
            <div
              className={`flex flex-col h-[calc(100vh-140px)] mb-2 overflow-x-auto justify-center items-center`}
            >
              {loading ? (
                <>
                  <p className="text-[#1F1F1F] text-[24px] font-medium mb-[28px]">
                    Loading...
                  </p>
                </>
              ) : (
                <>
                  <p className="text-[#1F1F1F] text-[24px] font-bold mb-[9px]">
                    No course assigned yet.
                  </p>
                  <p className="text-[#1F1F1F] text-[24px] font-medium mb-[28px]">
                    your course list and progress will appear here.
                  </p>

                  <Button
                    className="w-[195px] h-[48px] bg-[#5C88C5] text-white"
                    onClick={() => navigate(-1)}
                  >
                    Go Back
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col overflow-y-auto">
      {/* Unit navigation tabs */}

      <div
        className="h-[68px] px-[32px] items-center flex justify-start gap-4"
        style={{ backgroundColor: "rgba(255, 255, 255, 0.4)" }}
      >
        <div>
          <Breadcrumbs
            // title="Course List"
            onClick={() => navigate(-1)} //navigate("/course-management")}
            bg="#fff"
          />
        </div>

        <div className="flex overflow-x-auto">
          {headerMenus.map((unit) => (
            <button
              key={unit.key}
              onClick={() => {
                setSelectedTab(unit.key);
              }}
              className={`relative px-4 py-3 h-[68px] w-[150px] group text-sm font-medium whitespace-nowrap flex items-center justify-center ${selectedTab === unit.key ? "text-[#1F1F1F]" : "text-[#6B7280]"
                }`}
            >
              <div className="flex items-center gap-2">
                <span className="text-[#1F1F1F] group-hover:text-[#5C88C5] transition-all duration-300">
                  {unit.title}
                </span>
                {/* {courseCount > 0 && ( */}
                {getCount(unit.key) !== null && (
                  <span className="w-[30px] h-[30px] rounded-full flex items-center justify-center bg-[#5C88C5] text-white transition-all duration-300">
                    {getCount(unit.key)}
                  </span>
                )}
                {/* )} */}
              </div>
              {selectedTab === unit.key && (
                <div className="absolute bottom-0 left-0 w-full h-[10px] rounded-tl-[42px] rounded-tr-[42px] bg-[#5C88C5]"></div>
              )}
            </button>
          ))}
        </div>
      </div>

      <div className="px-[32px] py-[24px] ">
        {selectedTab === "unit" && selectedLesson && selectedUnit ? (
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-4">
            <h1 className="text-2xl font-semibold text-[#2E2E32]">
              {selectedUnit.name}
            </h1>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              {userRole?.code === ROLES.TEACHER && (
                <FilterWithRadio
                  showAbove={false}
                  options={students.map((student) => ({
                    value: student.user_id,
                    label: student.name,
                  }))}
                  value={selectedStudentId}
                  className="w-full sm:w-[235px]"
                  onChange={(value) => {
                    setSelectedStudentId(value);
                    dispatch(
                      refetchLessonUnitCount({
                        gradeId: id as string,
                        studentId: value,
                        viewType,
                      })
                    );
                  }}
                />
              )}
            </div>
          </div>
        ) : (
          <>
            {selectedTab === "assignment" && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-4">
                <h1 className="text-2xl font-semibold text-[#2E2E32]">
                  Unit Assignment
                </h1>
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <FilterWithRadio
                    showAbove={false}
                    options={[
                      {
                        value: "general",
                        label: "General Assignment",
                        count:
                          courseTabCount?.assignment?.general_assignment || "0",
                      },
                      {
                        value: "unit",
                        label: "Unit Assignment",
                        count:
                          courseTabCount?.assignment?.unit_assignment || "0",
                      },
                    ]}
                    popupWidth="250px"
                    value={selectedAssignmentType}
                    className="w-full sm:w-[250px]"
                    onChange={(value) => {
                      setSelectedAssignmentType(value);
                    }}
                  />
                  {userRole?.code === ROLES.TEACHER && (
                    <FilterWithRadio
                      showAbove={false}
                      options={students.map((student) => ({
                        value: student.user_id,
                        label: student.name,
                      }))}
                      value={selectedStudentId}
                      className="w-full sm:w-[235px]"
                      onChange={(value) => {
                        setSelectedStudentId(value);
                      }}
                    />
                  )}
                </div>
              </div>
            )}
            {(selectedTab === "feedback" || selectedTab === "evaluation") && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-4">
                {selectedTab === "feedback" ? <h1 className="text-2xl font-semibold text-[#2E2E32] mb-[15px]">
                  Queries Management
                </h1> : <div></div>}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  {userRole?.code === ROLES.TEACHER && (
                    <FilterWithRadio
                      showAbove={false}
                      options={students.map((student) => ({
                        value: student.user_id,
                        label: student.name,
                      }))}
                      value={selectedStudentId}
                      className="w-full sm:w-[235px]"
                      onChange={(value) => {
                        setSelectedStudentId(value);
                      }}
                    />
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* {courseData && ( */}
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left column - Video player */}
          <div className={`w-full ${selectedTab === "evaluation" ? 'lg:w-full' : 'lg:w-2/3'}`}>
            <div className="mb-6">
              {selectedTab === "unit" && <ScromFile unit={selectedUnit} />}

              {selectedTab === "assignment" && (
                <Assignments
                  recordType={viewType}
                  selectedAssignmentType={selectedAssignmentType}
                  selectedStudentId={selectedStudentId}
                  selectedLessonId={selectedLesson?.id || ""}
                  selectedUnitId={selectedUnit?.id || ""}
                />
              )}

              {selectedTab === "feedback" && (
                <>
                  <Queries
                    recordType={viewType}
                    selectedLessonId={selectedLesson?.id || ""}
                    selectedUnitId={selectedUnit?.id || ""}
                    selectedStudentId={selectedStudentId}
                    selectedAssignmentType={selectedAssignmentType}
                  />
                </>
              )}
              {selectedTab === "evaluation" && (
                <Evaluation
                  selectedStudentId={selectedStudentId || user?.user_id || ""}
                  courseData={courseData ?? undefined}
                  editable={userRole?.code === ROLES.TEACHER}
                  recordType={viewType}
                />
              )}

            </div>
          </div>

          {/* Right column - Study list */}
          {selectedTab !== "evaluation" && <div className="w-full lg:w-1/3">
            <StudyList
              title={courseData?.name || ""}
              gradeTitle={courseData?.name || ""}
              lessons={transformedLessons}
              terms={transformedTerms}
              activeLesson={selectedUnit?.id}
              onUnitSelect={onUnitSelect}
              showProgress={navigationSource !== "course-list"}
              selectedTab={selectedTab}
            />
          </div>}
        </div>
        {/* )} */}
      </div>
    </div>
  );
}

export default CourseDetail;
